import React, { useMemo, useState } from 'react';
import api from '../../Services/api';
import useAPI from '../../hooks/useAPI';
import endpointsAdmin from '../../Services/endpointAdmin';
import usePostDonatur from '../../hooks/usePostDonatur';
import usePostTransaksi from '../../hooks/usePostTransaksi';
import { addNotification } from '../../Services/notifikasi';
import { findFieldDeep, getAdminLembagaScope } from './adminScope';

const createEmptyTransaksiForm = () => ({
  jenis_donasi: '',
  detail_donasi: '',
  himpun: '',
  detail_himpun: '',
  nominal: '',
  status: 'berhasil',
  catatan: '',
});

const createKeranjangItem = (transaksiForm) => ({
  id: `trx-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  ...transaksiForm,
});

const requiredFieldProps = (message) => ({
  required: true,
  title: message,
  onInvalid: (event) => event.target.setCustomValidity(message),
  onInput: (event) => event.target.setCustomValidity(''),
});

function InputTransaksiDonasi({ admin }) {
  const { data: jenisDonasiData } = useAPI(endpointsAdmin.jenis_donasi?.getAll || '/api/jenis-donasi');
  const { data: detailDonasiData } = useAPI(endpointsAdmin.detail_donasi?.getAll || '/api/detail-donasi');
  const { data: himpunData } = useAPI(endpointsAdmin.himpun.getAll);
  const { data: detailHimpunData } = useAPI(endpointsAdmin.detail_himpun.getAll);
  const { data: donaturData } = useAPI(endpointsAdmin.donatur.getAll);
  const lembagaScope = useMemo(() => getAdminLembagaScope(admin), [admin]);
  const resolvedAdminLembagaId = useMemo(() => {
    if (lembagaScope.id) return String(lembagaScope.id).trim();

    try {
      const savedAdmin = localStorage.getItem('admin_user');
      if (!savedAdmin) return '';

      const parsedAdmin = JSON.parse(savedAdmin);
      return String(
        findFieldDeep(parsedAdmin, ['selected_id_lembaga', 'id_lembaga', 'idLembaga']) || ''
      ).trim();
    } catch (errorParsingAdmin) {
      return '';
    }
  }, [lembagaScope.id]);

  const { postDonatur, error: errorPostDonatur } = usePostDonatur(endpointsAdmin);
  const { postTransaksi } = usePostTransaksi(endpointsAdmin);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDonaturSuggestions, setShowDonaturSuggestions] = useState(false);
  

  const [donaturForm, setDonaturForm] = useState({
    kode_donatur: '',
    nik: '',
    nama: '',
    alamat: '',
    jenis_kelamin: 'Laki-laki',
    no_hp: '',
    email: '',
  });

  const [transaksiForm, setTransaksiForm] = useState(createEmptyTransaksiForm());
  const [keranjangTransaksi, setKeranjangTransaksi] = useState([]);

  const jenisDonasiList = useMemo(() => {
    if (Array.isArray(jenisDonasiData?.jenis_donasi)) return jenisDonasiData.jenis_donasi;
    if (Array.isArray(jenisDonasiData?.data)) return jenisDonasiData.data;
    if (Array.isArray(jenisDonasiData)) return jenisDonasiData;
    return [];
  }, [jenisDonasiData]);

  const himpunList = useMemo(() => {
    if (Array.isArray(himpunData?.himpun)) return himpunData.himpun;
    if (Array.isArray(himpunData?.data)) return himpunData.data;
    if (Array.isArray(himpunData)) return himpunData;
    return [];
  }, [himpunData]);

  const allDetailDonasiList = useMemo(() => {
    if (Array.isArray(detailDonasiData?.detail_donasi)) return detailDonasiData.detail_donasi;
    if (Array.isArray(detailDonasiData?.data)) return detailDonasiData.data;
    if (Array.isArray(detailDonasiData)) return detailDonasiData;
    return [];
  }, [detailDonasiData]);

  const detailHimpunList = useMemo(() => {
    if (Array.isArray(detailHimpunData?.detail_himpun)) return detailHimpunData.detail_himpun;
    if (Array.isArray(detailHimpunData?.data)) return detailHimpunData.data;
    if (Array.isArray(detailHimpunData)) return detailHimpunData;
    return [];
  }, [detailHimpunData]);

  const donaturList = useMemo(() => {
    if (Array.isArray(donaturData?.donatur)) return donaturData.donatur;
    if (Array.isArray(donaturData?.data)) return donaturData.data;
    if (Array.isArray(donaturData)) return donaturData;
    return [];
  }, [donaturData]);

  const getFilteredDetailDonasiList = (jenisDonasiValue) => {
    if (!jenisDonasiValue) return [];

    const selectedJenis = jenisDonasiList.find((item) => {
      const selectedValue = String(jenisDonasiValue || '').trim();
      return [
        item?.kode_jenis_donasi,
        item?.kode,
        item?.id_jenis_donasi,
        item?.id,
      ].some((value) => String(value || '').trim() === selectedValue);
    });

    if (!selectedJenis) return [];

    const jenisId = String(selectedJenis?.id || selectedJenis?.id_jenis_donasi || '').trim();
    const jenisCode = String(selectedJenis?.kode_jenis_donasi || selectedJenis?.kode || '').trim();

    return allDetailDonasiList.filter((detail) => {
      const detailJenisId = String(detail?.id_jenis_donasi || detail?.jenis_donasi_id || '').trim();
      const detailJenisCode = String(detail?.kode_jenis_donasi || detail?.kode_jenis || '').trim();

      return (
        (jenisId && detailJenisId === jenisId) ||
        (jenisCode && detailJenisCode === jenisCode)
      );
    });
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(Number(value || 0) || 0);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
  const phoneRegex = /^(\+62|0)[0-9]{9,12}$/;

  const getJenisCode = (item) =>
    String(item?.kode_jenis_donasi || item?.kode || item?.id_jenis_donasi || item?.id || '').trim();

  const getDetailCode = (item) =>
    String(item?.kode_detail_donasi || item?.kode || item?.id_detail_donasi || item?.id || '').trim();

  const getHimpunCode = (item) =>
    String(item?.kode_himpun || item?.kode || item?.id_himpun || item?.id || '').trim();

  const getHimpunName = (item) =>
    String(item?.nama_himpun || item?.nama || '').trim();

  const isQrHimpun = (item) => {
    const label = String(item?.nama_himpun || item?.nama || item?.kode_himpun || item?.kode || '').trim().toLowerCase();
    return label.includes('qris') || label.includes('qr');
  };

  const getDetailHimpunCode = (item) =>
    String(item?.kode_detail_himpun || item?.kode || item?.id_detail_himpun || item?.id || '').trim();

  const decodeHimpunValue = (value) => {
    const [himpunCode, detailHimpunCode] = String(value || '').split('|');
    return {
      himpunCode: String(himpunCode || '').trim(),
      detailHimpunCode: String(detailHimpunCode || '').trim(),
    };
  };

  const getDetailHimpunName = (item) =>
    String(item?.nama || item?.nama_detail_himpun || item?.kode_detail_himpun || item?.kode || item?.id_detail_himpun || item?.id || '').trim();

  const findSelectedHimpun = (himpunValue) => {
    if (!himpunValue) return null;
    const selectedValue = String(himpunValue || '').trim();
    return himpunList.find((item) =>
      [item?.kode_himpun, item?.kode, item?.id_himpun, item?.id]
        .some((value) => String(value || '').trim() === selectedValue)
    );
  };

  const getFilteredDetailHimpunList = (himpunValue) => {
    const selectedHimpun = findSelectedHimpun(himpunValue);
    if (!selectedHimpun) return [];

    const selectedHimpunCode = String(selectedHimpun?.kode_himpun || selectedHimpun?.kode || '').trim();
    const selectedHimpunId = String(selectedHimpun?.id_himpun || selectedHimpun?.id || '').trim();

    return detailHimpunList.filter((item) => {
      const itemHimpunCode = String(item?.kode_himpun || '').trim();
      const itemHimpunId = String(item?.id_himpun || '').trim();
      return (
        (selectedHimpunCode && itemHimpunCode && itemHimpunCode === selectedHimpunCode) ||
        (selectedHimpunId && itemHimpunId && itemHimpunId === selectedHimpunId)
      );
    });
  };

  const encodeHimpunValue = (himpunCode, detailHimpunCode) =>
    `${String(himpunCode || '').trim()}|${String(detailHimpunCode || '').trim()}`;

  const extractDonaturEntity = (response) => {
    if (!response || typeof response !== 'object') return null;

    return (
      response?.donatur ||
      response?.data?.donatur ||
      response?.result?.donatur ||
      response?.payload?.donatur ||
      response?.data ||
      response?.result ||
      response?.payload ||
      response
    );
  };

  const extractKodeDonatur = (response) => {
    const donaturEntity = extractDonaturEntity(response);

    return String(
      donaturEntity?.kode_donatur ||
      donaturEntity?.kodeDonatur ||
      donaturEntity?.kode ||
      response?.kode_donatur ||
      response?.kodeDonatur ||
      response?.data?.kode_donatur ||
      response?.data?.kodeDonatur ||
      response?.result?.kode_donatur ||
      response?.result?.kodeDonatur ||
      response?.payload?.kode_donatur ||
      response?.payload?.kodeDonatur ||
      ''
    ).trim();
  };

  const findExistingDonatur = () => {
    const normalizedKode = String(donaturForm.kode_donatur || '').trim();
    const normalizedNik = String(donaturForm.nik || '').trim();
    const normalizedEmail = String(donaturForm.email || '').trim().toLowerCase();
    const normalizedPhone = String(donaturForm.no_hp || '').replace(/\s/g, '');

    return (
      donaturList.find((item) => {
        const itemKode = String(item?.kode_donatur || item?.kodeDonatur || item?.kode || '').trim();
        const itemNik = String(item?.nik || '').trim();
        const itemEmail = String(item?.email || '').trim().toLowerCase();
        const itemPhone = String(item?.no_hp || item?.nomor_hp || item?.phone || '').replace(/\s/g, '');

        return (
          (normalizedKode && itemKode === normalizedKode) ||
          (normalizedNik && itemNik === normalizedNik) ||
          (normalizedEmail && itemEmail === normalizedEmail) ||
          (normalizedPhone && itemPhone === normalizedPhone)
        );
      }) || null
    );
  };

  const suggestedDonaturList = useMemo(() => {
    const keyword = String(donaturForm.nama || '').trim().toLowerCase();

    if (!keyword) {
      return donaturList.slice(0, 6);
    }

    return donaturList
      .filter((item) =>
        String(item?.nama || '')
          .trim()
          .toLowerCase()
          .includes(keyword)
      )
      .slice(0, 6);
  }, [donaturForm.nama, donaturList]);

  const fillDonaturFormFromExisting = (item) => {
    if (!item) return;

    setDonaturForm((prev) => ({
      ...prev,
      nik: String(item?.nik || '').trim(),
      nama: String(item?.nama || '').trim(),
      alamat: String(item?.alamat || '').trim(),
      jenis_kelamin: String(item?.jenis_kelamin || prev.jenis_kelamin || 'Laki-laki').trim(),
      no_hp: String(item?.no_hp || item?.nomor_hp || '').trim(),
      email: String(item?.email || '').trim(),
      kode_donatur: String(item?.kode_donatur || item?.kodeDonatur || item?.kode || '').trim(),
    }));
    setShowDonaturSuggestions(false);
    setError('');
    setSuccessMessage('');
  };

  const handleDonaturChange = (event) => {
    const { name, value } = event.target;
    setDonaturForm((prev) => ({ ...prev, [name]: value }));

    if (name === 'nama') {
      setShowDonaturSuggestions(true);

      const exactMatch = donaturList.find(
        (item) =>
          String(item?.nama || '').trim().toLowerCase() ===
          String(value || '').trim().toLowerCase()
      );

      if (exactMatch) {
        fillDonaturFormFromExisting(exactMatch);
        return;
      }
    }

    setError('');
    setSuccessMessage('');
  };

  const handleTransaksiChange = (event) => {
    const { name, value } = event.target;

    setTransaksiForm((prev) => {
      if (name === 'himpun') {
        const { himpunCode, detailHimpunCode } = decodeHimpunValue(value);
        return {
          ...prev,
          himpun: himpunCode,
          detail_himpun: detailHimpunCode,
        };
      }

      if (name === 'nominal') {
        return {
          ...prev,
          nominal: String(value || '').replace(/\D/g, ''),
        };
      }

      return {
        ...prev,
        [name]: value,
        ...(name === 'jenis_donasi' ? { detail_donasi: '' } : {}),
      };
    });

    setError('');
    setSuccessMessage('');
  };

  const validateDonaturForm = () => {
    if (!donaturForm.nama.trim()) return 'Nama donatur wajib diisi.';
    if (!donaturForm.jenis_kelamin.trim()) return 'Jenis kelamin wajib diisi.';
    if (!donaturForm.no_hp.trim()) return 'Nomor handphone wajib diisi.';
    if (!donaturForm.email.trim()) return 'Email wajib diisi.';
    if (!emailRegex.test(donaturForm.email.trim())) return 'Format email donatur tidak valid.';
    if (!phoneRegex.test(donaturForm.no_hp.replace(/\s/g, ''))) {
      return 'Format nomor handphone tidak valid. Gunakan +62xxx atau 0xxx.';
    }
    return '';
  };

  const validateSingleTransaksi = (item, nomor = null) => {
    const suffix = nomor ? ` pada transaksi ke-${nomor}` : '';
    const selectedHimpun = findSelectedHimpun(item.himpun);
    const availableDetailHimpun = getFilteredDetailHimpunList(item.himpun);
    const requiresDetailHimpun = availableDetailHimpun.length > 0;

    if (!item.jenis_donasi) return `Jenis donasi${suffix} wajib dipilih.`;
    if (!item.detail_donasi) return `Program donasi${suffix} wajib dipilih.`;
    if (!item.himpun) return `Metode pembayaran${suffix} wajib dipilih.`;
    if (selectedHimpun && requiresDetailHimpun && !item.detail_himpun) return `Detail metode pembayaran${suffix} wajib dipilih.`;
    if (!item.nominal || Number(item.nominal) <= 0) {
      return `Nominal donasi${suffix} harus lebih dari 0.`;
    }
    return '';
  };

  const handleAddToKeranjang = () => {
    const validationError = validateSingleTransaksi(transaksiForm);
    if (validationError) {
      setError(validationError);
      return;
    }

    setKeranjangTransaksi((prev) => [...prev, createKeranjangItem(transaksiForm)]);
    setTransaksiForm(createEmptyTransaksiForm());
    setError('');
    setSuccessMessage('Item donasi berhasil ditambahkan ke keranjang.');
  };

  const handleRemoveTransaksi = (id) => {
    setKeranjangTransaksi((prev) => prev.filter((item) => item.id !== id));
    setError('');
    setSuccessMessage('');
  };

  const getSubmissionTransaksiList = () => {
    if (keranjangTransaksi.length > 0) return keranjangTransaksi;
    return [createKeranjangItem(transaksiForm)];
  };

  const generateTransactionQrCode = async (nominal) => {
    const lembagaId = String(resolvedAdminLembagaId || '').trim();
    if (!lembagaId) {
      throw new Error('ID lembaga admin tidak ditemukan. Silakan login ulang lalu coba lagi.');
    }

    const qrPayload = {
      id_lembaga: lembagaId,
      nominal: String(nominal || '').trim(),
    };

    const response = await api.post(endpointsAdmin.qrcode.generate, qrPayload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response?.data || null;
  };

  const extractGeneratedQrisString = (response) =>
    String(
      response?.qris ||
      response?.data?.qris ||
      response?.result?.qris ||
      response?.payload?.qris ||
      ''
    ).trim();

  const validateTransaksiForms = (items) => {
    if (!items.length) {
      return 'Transaksi donasi masih kosong.';
    }

    for (let index = 0; index < items.length; index += 1) {
      const nomor = items.length > 1 ? index + 1 : null;
      const validationError = validateSingleTransaksi(items[index], nomor);
      if (validationError) return validationError;
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');
    setShowSuccessModal(false);

    const validationError = validateDonaturForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const transaksiItems = getSubmissionTransaksiList();
    const transaksiValidationError = validateTransaksiForms(transaksiItems);
    if (transaksiValidationError) {
      setError(transaksiValidationError);
      return;
    }

    setLoading(true);

    try {
      const existingDonatur = findExistingDonatur();
      let kodeDonatur = String(
        existingDonatur?.kode_donatur ||
        existingDonatur?.kodeDonatur ||
        existingDonatur?.kode ||
        ''
      ).trim();
      const isExistingDonatur = Boolean(kodeDonatur);

      if (!kodeDonatur) {
        const donaturResult = await postDonatur(
          donaturForm.nik,
          donaturForm.nama,
          donaturForm.alamat,
          '',
          '',
          donaturForm.jenis_kelamin,
          '',
          '',
          '',
          '',
          donaturForm.no_hp,
          donaturForm.email,
          '',
          resolvedAdminLembagaId,
          0
        );

        kodeDonatur = extractKodeDonatur(donaturResult);

        if (!donaturResult || !kodeDonatur) {
          throw new Error(errorPostDonatur || 'Data donatur berhasil dikirim, tetapi kode donatur tidak diterima.');
        }
      }

      const savedTransactionCodes = [];
      const generatedQrisPayloads = [];

      for (let index = 0; index < transaksiItems.length; index += 1) {
        const transaksiItem = transaksiItems[index];
        const filteredDetailDonasiList = getFilteredDetailDonasiList(transaksiItem.jenis_donasi);
        const selectedJenis = jenisDonasiList.find(
          (item) => getJenisCode(item) === String(transaksiItem.jenis_donasi).trim()
        );
        const selectedDetail = filteredDetailDonasiList.find(
          (item) => getDetailCode(item) === String(transaksiItem.detail_donasi).trim()
        );
        const selectedHimpun = himpunList.find(
          (item) => getHimpunCode(item) === String(transaksiItem.himpun).trim()
        );
        const selectedDetailHimpun = getFilteredDetailHimpunList(transaksiItem.himpun).find(
          (item) => getDetailHimpunCode(item) === String(transaksiItem.detail_himpun).trim()
        );
        const requiresDetailHimpun = getFilteredDetailHimpunList(transaksiItem.himpun).length > 0;

        if (
          !selectedJenis ||
          !selectedDetail ||
          !selectedHimpun ||
          (requiresDetailHimpun && !selectedDetailHimpun)
        ) {
          throw new Error(`Pilihan transaksi ke-${index + 1} tidak valid. Periksa jenis donasi, program, metode pembayaran, dan detail metode pembayaran.`);
        }

        // Skip QRIS generation for now (QRIS modal removed / QRIS option hidden)

        const kodeTransaksi = `TRX-${Date.now()}-${index + 1}-ADM`;
        const result = await postTransaksi(
          kodeTransaksi,
          kodeDonatur,
          getJenisCode(selectedJenis),
          getDetailCode(selectedDetail),
          String(admin?.kode_user || '').trim(),
          resolvedAdminLembagaId,
          getHimpunCode(selectedHimpun),
          requiresDetailHimpun ? getDetailHimpunCode(selectedDetailHimpun) : '',
          Number(transaksiItem.nominal),
          transaksiItem.status,
          String(transaksiItem.catatan || '').trim(),
          0,
          getHimpunName(selectedHimpun),
          requiresDetailHimpun ? getDetailHimpunName(selectedDetailHimpun) : ''
        );

        const transaksiDataResult = result?.transaksi || result?.data || result;
        const kodeTrx = transaksiDataResult?.kode_transaksi || kodeTransaksi;
        savedTransactionCodes.push(kodeTrx);

        addNotification({
          title: 'Transaksi Donasi Diterima',
          message: `Terima kasih telah berdonasi sebesar ${formatCurrency(transaksiItem.nominal)}. Donasi Anda sedang diproses.`,
          userType: 'donatur',
          audienceKey: kodeDonatur,
        });
      }

      setSuccessMessage(
        `${isExistingDonatur ? 'Donatur lama ditemukan.' : 'Data donatur baru berhasil disimpan.'} ${transaksiItems.length} transaksi donasi berhasil dibuat. Kode donatur: ${kodeDonatur}. Kode transaksi: ${savedTransactionCodes.join(', ')}`
      );
      setShowSuccessModal(true);
      handleReset(true);
    } catch (err) {
      setError(err.message || 'Gagal menyimpan data donatur dan transaksi offline.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (preserveMessages = false) => {
    setDonaturForm({
      kode_donatur: '',
      nik: '',
      nama: '',
      alamat: '',
      jenis_kelamin: 'Laki-laki',
      no_hp: '',
      email: '',
    });
    setTransaksiForm(createEmptyTransaksiForm());
    setKeranjangTransaksi([]);
    if (!preserveMessages) {
      setError('');
      setSuccessMessage('');
      setShowSuccessModal(false);
    }
  };


  return (
    <div className="min-h-screen bg-slate-50/70 px-4 sm:px-6 lg:px-12 py-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Page Header */}
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 via-blue-900 to-blue-950 text-white shadow-[0_20px_50px_rgba(15,23,42,0.15)]">
          <div className="px-8 py-10 lg:py-12">
            <div className="inline-block rounded-full bg-white/10 backdrop-blur px-4 py-2 text-xs font-semibold uppercase tracking-wider text-blue-200 mb-4">
              Pencatatan Transaksi Admin
            </div>
            <h1 className="text-3xl font-bold leading-tight lg:text-4xl">
              Input Transaksi Donasi
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-200">
              Input data donatur dan catat transaksi pembayaran donasi secara cepat, baik transaksi tunggal maupun beberapa transaksi sekaligus.
            </p>
          </div>
        </section>

        {/* Step Cards Progress */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Langkah 1</span>
            <h3 className="mt-1 text-sm font-bold text-slate-900">Identitas Donatur</h3>
            <p className="mt-1 text-xs text-slate-500">Pilih donatur terdaftar atau input data baru.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Langkah 2</span>
            <h3 className="mt-1 text-sm font-bold text-slate-900">Detail Transaksi</h3>
            <p className="mt-1 text-xs text-slate-500">Pilih jenis donasi, program, dan metode pembayaran.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Langkah 3</span>
            <h3 className="mt-1 text-sm font-bold text-slate-900">Simpan Data</h3>
            <p className="mt-1 text-xs text-slate-500">Simpan langsung atau kumpulkan di keranjang.</p>
          </div>
        </div>

        {/* Action Feedback Alerts */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-xs sm:text-sm font-semibold text-red-700">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-xs sm:text-sm font-semibold text-emerald-700">
            {successMessage}
          </div>
        )}

        {/* Main Form Section */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section 1: Data Donatur */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-5">
            <div className="border-b border-slate-100 pb-3">
              <h2 className="text-lg font-bold text-slate-900">Data Diri Donatur</h2>
              <p className="text-xs text-slate-500">Isi data lengkap donatur atau ketik nama untuk memilih donatur lama.</p>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  NIK (Opsional)
                </label>
                <input
                  type="text"
                  name="nik"
                  value={donaturForm.nik}
                  onChange={handleDonaturChange}
                  placeholder="Masukkan NIK 16 digit"
                  maxLength="16"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Nama Donatur <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="nama"
                    value={donaturForm.nama}
                    onChange={handleDonaturChange}
                    onFocus={() => setShowDonaturSuggestions(true)}
                    onBlur={() => {
                      window.setTimeout(() => setShowDonaturSuggestions(false), 150);
                    }}
                    placeholder="Ketik nama lengkap donatur"
                    autoComplete="off"
                    {...requiredFieldProps('Nama donatur wajib diisi.')}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  {showDonaturSuggestions && suggestedDonaturList.length > 0 ? (
                    <div className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-20 max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
                      {suggestedDonaturList.map((item) => {
                        const itemKode = String(item?.kode_donatur || item?.kode || '').trim();
                        const itemNama = String(item?.nama || '').trim();
                        const itemEmail = String(item?.email || '').trim();
                        const itemPhone = String(item?.no_hp || item?.nomor_hp || '').trim();

                        return (
                          <button
                            key={`${itemKode || itemNama}-${itemEmail || itemPhone}`}
                            type="button"
                            onMouseDown={() => fillDonaturFormFromExisting(item)}
                            className="flex w-full flex-col border-b border-slate-100 px-4 py-2.5 text-left last:border-b-0 hover:bg-slate-50 transition"
                          >
                            <span className="text-xs font-bold text-slate-900">{itemNama || 'Tanpa Nama'}</span>
                            <span className="mt-0.5 text-[11px] text-slate-500">
                              {itemKode || '-'} {itemEmail ? `• ${itemEmail}` : ''} {itemPhone ? `• ${itemPhone}` : ''}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Jenis Kelamin <span className="text-red-500">*</span>
                </label>
                <select
                  name="jenis_kelamin"
                  value={donaturForm.jenis_kelamin}
                  onChange={handleDonaturChange}
                  {...requiredFieldProps('Jenis kelamin wajib dipilih.')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="Laki-laki">Laki-laki</option>
                  <option value="Perempuan">Perempuan</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  No. Handphone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="no_hp"
                  value={donaturForm.no_hp}
                  onChange={handleDonaturChange}
                  placeholder="Contoh: 081234567890"
                  {...requiredFieldProps('Nomor handphone wajib diisi.')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={donaturForm.email}
                  onChange={handleDonaturChange}
                  placeholder="Contoh: donatur@email.com"
                  {...requiredFieldProps('Email donatur wajib diisi.')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Alamat (Opsional)
                </label>
                <input
                  type="text"
                  name="alamat"
                  value={donaturForm.alamat}
                  onChange={handleDonaturChange}
                  placeholder="Alamat domisili donatur"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </section>

          {/* Section 2: Form Transaksi Donasi */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-5">
            <div className="border-b border-slate-100 pb-3">
              <h2 className="text-lg font-bold text-slate-900">Form Detail Transaksi</h2>
              <p className="text-xs text-slate-500">Isi rincian transaksi donasi. Untuk menyimpan lebih dari satu transaksi, gunakan tombol 'Tambah ke Keranjang'.</p>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Jenis Donasi <span className="text-red-500">*</span>
                </label>
                <select
                  name="jenis_donasi"
                  value={transaksiForm.jenis_donasi}
                  onChange={handleTransaksiChange}
                  {...requiredFieldProps('Jenis donasi wajib dipilih.')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">-- Pilih Jenis Donasi --</option>
                  {jenisDonasiList.map((jenis) => (
                    <option key={getJenisCode(jenis)} value={getJenisCode(jenis)}>
                      {jenis?.nama_donasi || jenis?.nama}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Program Donasi <span className="text-red-500">*</span>
                </label>
                <select
                  name="detail_donasi"
                  value={transaksiForm.detail_donasi}
                  onChange={handleTransaksiChange}
                  {...requiredFieldProps('Program donasi wajib dipilih.')}
                  disabled={!transaksiForm.jenis_donasi}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
                >
                  <option value="">-- Pilih Program Donasi --</option>
                  {getFilteredDetailDonasiList(transaksiForm.jenis_donasi).map((detail) => (
                    <option key={getDetailCode(detail)} value={getDetailCode(detail)}>
                      {detail?.nama_detail_donasi || detail?.nama}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Metode Pembayaran <span className="text-red-500">*</span>
                </label>
                <select
                  name="himpun"
                  value={encodeHimpunValue(transaksiForm.himpun, transaksiForm.detail_himpun)}
                  onChange={handleTransaksiChange}
                  {...requiredFieldProps('Metode pembayaran wajib dipilih.')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">-- Pilih Metode Pembayaran --</option>
                  {himpunList.map((himpun) => {
                    // Hide QRIS / QR options from payment method list
                    if (isQrHimpun(himpun)) return null;

                    const bankDetails = getFilteredDetailHimpunList(getHimpunCode(himpun)) || [];
                    const himpunName = getHimpunName(himpun) || 'Metode Pembayaran';

                    const filteredBankDetails = bankDetails.filter((detail) => {
                      const label = String(detail?.nama || detail?.nama_detail_himpun || detail?.kode || detail?.kode_detail_himpun || '').trim().toLowerCase();
                      return !(label.includes('qris') || label.includes('qr'));
                    });

                    if (filteredBankDetails.length) {
                      return (
                        <optgroup key={getHimpunCode(himpun)} label={himpunName}>
                          {filteredBankDetails.map((detail) => {
                            const detailName = getDetailHimpunName(detail);
                            return (
                              <option
                                key={`${getHimpunCode(himpun)}-${getDetailHimpunCode(detail)}`}
                                value={encodeHimpunValue(getHimpunCode(himpun), getDetailHimpunCode(detail))}
                              >
                                {detailName ? `${himpunName} - ${detailName}` : himpunName}
                              </option>
                            );
                          })}
                        </optgroup>
                      );
                    }

                    return (
                      <option key={getHimpunCode(himpun)} value={encodeHimpunValue(getHimpunCode(himpun), '')}>
                        {himpunName}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Nominal Donasi (Rp) <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs sm:text-sm text-slate-900 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                  <span className="font-bold text-slate-500 mr-2">Rp</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="nominal"
                    value={transaksiForm.nominal}
                    onChange={handleTransaksiChange}
                    placeholder="0"
                    {...requiredFieldProps('Nominal donasi wajib diisi.')}
                    className="w-full bg-transparent outline-none font-bold text-slate-900"
                  />
                </div>
                {transaksiForm.nominal && (
                  <p className="mt-1 text-xs font-bold text-blue-600">
                    Formatted: {formatCurrency(transaksiForm.nominal)}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Status Pembayaran <span className="text-red-500">*</span>
                </label>
                <select
                  name="status"
                  value={transaksiForm.status}
                  onChange={handleTransaksiChange}
                  {...requiredFieldProps('Status pembayaran wajib dipilih.')}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="berhasil">Berhasil</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Catatan Transaksi (Opsional)
                </label>
                <input
                  type="text"
                  name="catatan"
                  value={transaksiForm.catatan}
                  onChange={handleTransaksiChange}
                  placeholder="Catatan khusus transaksi..."
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Keranjang Action */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-t border-slate-100 pt-4">
              <p className="text-xs text-slate-500">Ingin menginput beberapa donasi sekaligus? Tambahkan item di atas ke keranjang.</p>
              <button
                type="button"
                onClick={handleAddToKeranjang}
                className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-bold text-blue-700 hover:bg-blue-100 transition whitespace-nowrap"
              >
                + Tambah ke Keranjang
              </button>
            </div>

            {/* Keranjang Transaksi List */}
            {keranjangTransaksi.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Keranjang Transaksi ({keranjangTransaksi.length} item)
                  </h3>
                </div>

                <div className="space-y-2">
                  {keranjangTransaksi.map((item, index) => {
                    const jenis = jenisDonasiList.find((data) => getJenisCode(data) === String(item.jenis_donasi).trim());
                    const detail = allDetailDonasiList.find((data) => getDetailCode(data) === String(item.detail_donasi).trim());
                    const himpun = himpunList.find((data) => getHimpunCode(data) === String(item.himpun).trim());

                    return (
                      <div key={item.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-xs">
                        <div>
                          <p className="font-bold text-slate-900">
                            #{index + 1} {jenis?.nama_donasi || jenis?.nama || 'Donasi'} - {detail?.nama_detail_donasi || detail?.nama || '-'}
                          </p>
                          <p className="text-slate-500 text-[11px] mt-0.5">
                            Metode: {getHimpunName(himpun)} • Status: {item.status}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-slate-900">{formatCurrency(item.nominal)}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTransaksi(item.id)}
                            className="text-red-600 font-bold hover:underline"
                          >
                            Hapus
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* Form Action Buttons */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm hover:bg-blue-700 disabled:opacity-70 transition"
            >
              {loading ? 'Menyimpan Transaksi...' : 'Simpan Donatur dan Transaksi'}
            </button>
          </div>
        </form>
      </div>

      {/* QRIS POPUP MODAL */}
      

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl text-center space-y-4">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-xl font-bold">
              ✓
            </div>
            <h3 className="text-lg font-bold text-slate-900">Transaksi Berhasil Disimpan</h3>
            <p className="text-xs text-slate-600 leading-relaxed">{successMessage}</p>
            <button
              type="button"
              onClick={() => setShowSuccessModal(false)}
              className="mt-2 w-full rounded-xl bg-blue-600 py-2.5 text-xs font-bold text-white hover:bg-blue-700 transition"
            >
              Selesai & Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default InputTransaksiDonasi;
